/* Public programme and projects are separate from the canonical chronological Floor. */
(() => {
  "use strict";
  const $ = (id) => document.getElementById(id);
  const make = (tag, cls, text) => {
    const node = document.createElement(tag);
    node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };
  const promptLabels = { feedback: "Feedback welcome", choice: "Help choose a version", remix: "Share your version", question: "Join the conversation" };
  let context, generation = 0, refreshTimer, agendaTimer, lastViewerKey, visible = false, surface = null;
  let projectsLoaded = false;
  let unread = [], marking = null, refreshFlight = null, refreshPending = false;
  function viewerKey() {
    const viewer = context.viewer();
    return JSON.stringify([viewer?.id || null, viewer?.status || null, Boolean(viewer?.email_verified)]);
  }
  function button(text, action, cls = "text-button") {
    const node = make("button", cls, text);
    node.type = "button";
    node.addEventListener("click", action);
    return node;
  }
  function dateLabel(value) {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : "Time unavailable";
  }
  function failure(node, message, retry = refresh) {
    node.replaceChildren(make("p", "home-note", message), button("Try again", retry));
  }
  function eventCard(event, label) {
    const card = make("article", "home-event");
    card.append(make("small", "home-eyebrow", label), make("h3", "", event.title), make("p", "", event.summary));
    const time = make("time", "home-note", dateLabel(event.starts_at));
    time.dateTime = event.starts_at;
    card.append(time);
    if (event.target?.id) card.append(button(label === "Recently together" ? "Revisit this moment" : "See what is happening", () => context.openDrop(event.target.id)));
    else if (label === "Happening now") card.append(button("Join the public chat", () => context.openChat(), "secondary-button"));
    return card;
  }
  function renderAgenda(data) {
    clearTimeout(agendaTimer);
    const serverNow = Date.parse(data.server_time);
    const deadlines = [data.current?.ends_at, data.next?.starts_at].map(Date.parse)
      .filter((value) => Number.isFinite(value) && value > serverNow);
    if (deadlines.length) agendaTimer = setTimeout(queueRefresh, Math.min(60_000, Math.max(250, Math.min(...deadlines) - serverNow + 100)));
    const node = $("home-programme");
    node.replaceChildren();
    if (data.current) node.append(eventCard(data.current, "Happening now"));
    if (data.next) node.append(eventCard(data.next, "Coming up"));
    if (data.recent?.length) {
      const archive = make("details", "home-recaps");
      archive.append(make("summary", "", "From recent shared moments"));
      data.recent.forEach((item) => archive.append(eventCard(item, "Recently together")));
      node.append(archive);
    }
    $("home-hub").hidden = surface !== "home" || !node.children.length;
  }
  function updateText(node, value) {
    if (node.textContent !== value) node.textContent = value;
  }
  function reconcileCards(node, cards) {
    const focused = document.activeElement;
    const focusIndex = Array.from(node.children).findIndex((child) => child.contains(focused));
    const keep = new Set(cards);
    cards.forEach((card, index) => {
      // Moving even the same button through append can blur it. Leave an unchanged
      // row in place, including its descendants and current keyboard focus.
      if (node.children[index] !== card) node.insertBefore(card, node.children[index] || null);
    });
    for (const child of Array.from(node.children)) if (!keep.has(child)) child.remove();
    if (focusIndex < 0 || document.activeElement !== document.body || document.querySelector("dialog[open]")) return;
    const target = node.contains(focused) ? focused
      : cards[Math.min(focusIndex, cards.length - 1)]?._home.action || $("creative-start-project");
    if (target && !target.closest("[hidden]")) target.focus({ preventScroll: true });
  }
  function renderProjects(data) {
    const node = $("home-project-list");
    const existing = new Map(Array.from(node.children).filter((card) => card._home).map((card) => [card._home.id, card]));
    const cards = (data.items || []).map((drop) => {
      let card = existing.get(drop.id);
      if (!card) {
        card = make("article", "home-project-card");
        const title = button("", () => context.openProject(card._home.id), "home-project-title");
        const name = make("span", "home-project-name");
        const author = make("span", "home-project-author"), prompt = make("span", "home-project-prompt");
        const meta = make("span", "home-project-meta");
        meta.append(author, prompt); title.append(name, meta); card.append(title);
        card._home = { id: drop.id, action: title, name, author, prompt };
      }
      updateText(card._home.name, drop.title);
      updateText(card._home.author, drop.author?.display_name || "Creator");
      updateText(card._home.prompt, promptLabels[drop.journey?.prompt] || "Work in progress");
      return card;
    });
    reconcileCards(node, cards);
    projectsLoaded = true;
    $("home-projects").hidden = !cards.length;
  }
  function clearProjects() {
    projectsLoaded = false;
    $("home-projects").hidden = true;
    $("home-project-list").replaceChildren();
  }
  function clearPrivate() {
    unread = [];
    $("home-catchup-list").replaceChildren();
    $("home-catchup-count").textContent = "";
    $("home-catchup-status").textContent = "";
    $("home-catchup-read").hidden = true;
    $("home-catchup").hidden = true;
  }
  function renderUpdates(data) {
    unread = data.items || [];
    const node = $("home-catchup-list");
    const existing = new Map(Array.from(node.children).filter((item) => item._home).map((item) => [item._home.id, item]));
    const cards = unread.map((drop) => {
      let item = existing.get(drop.id);
      if (!item) {
        item = button("", () => context.openProject(item._home.root), "home-update");
        const label = make("small", "home-eyebrow"), title = make("strong", ""), note = make("span", "home-note");
        item.append(label, title, note);
        item._home = { id: drop.id, action: item, label, title, note };
      }
      item._home.root = drop.journey.root_drop_id;
      updateText(item._home.label, drop.journey.mode === "response" ? "A response to your project" : "A creator you follow has an update");
      updateText(item._home.title, drop.title);
      updateText(item._home.note, `${drop.author.display_name} · ${dateLabel(drop.created_at)}`);
      return item;
    });
    reconcileCards(node, cards);
    $("home-catchup").hidden = !unread.length;
    $("home-catchup-count").textContent = data.unread_count ? `${data.unread_count} unread` : "";
    $("home-catchup-status").textContent = "";
    if (!unread.length) node.append(make("p", "home-note", "You are caught up. Follow a project to find its next update here."));
    $("home-catchup-read").hidden = !unread.length;
    $("home-catchup-read").disabled = Boolean(marking);
  }
  async function markDisplayedRead() {
    if (marking || !unread.length || !context.viewer()?.email_verified) return;
    const ids = unread.map((item) => item.id), owner = context.viewer().id;
    const operation = { owner };
    marking = operation; $("home-catchup-read").disabled = true;
    try {
      await context.api("/api/journeys/updates/read", { method: "POST", csrf: true, json: { drop_ids: ids } });
      if (context.viewer()?.id !== owner || marking !== operation) return;
      document.dispatchEvent(new CustomEvent("louderr:projects-read"));
    } catch (_) {
      if (context.viewer()?.id === owner && marking === operation) $("home-catchup-status").textContent = "Could not save read state. Try again; these updates remain in the list.";
    } finally {
      if (marking === operation) { marking = null; $("home-catchup-read").disabled = false; }
    }
  }
  function stopRefreshRead() {
    const flight = refreshFlight;
    refreshFlight = null;
    flight?.controller.abort();
  }
  async function refresh({ preserve = false } = {}) {
    if (!context) return;
    const key = viewerKey();
    if (key !== lastViewerKey) { clearPrivate(); clearProjects(); marking = null; lastViewerKey = key; preserve = false; }
    clearTimeout(refreshTimer); refreshTimer = null;
    stopRefreshRead();
    const request = ++generation;
    if (!visible) return;
    const flight = { controller: new AbortController(), key }; refreshFlight = flight;
    const current = () => !flight.controller.signal.aborted && request === generation && viewerKey() === key && visible;
    const verified = Boolean(context.viewer()?.email_verified);
    // Privacy/manual revalidation clears stale content. Ordinary new activity keeps cards
    // visible while one coalesced request runs; failures still replace stale cards.
    // Home shows the shared programme; Creative shows projects and, for verified members, their updates.
    const onHome = surface === "home", onCreative = surface === "creative";
    if (!preserve) {
      if (onCreative) {
        // The two Creative actions are available immediately. Reveal optional
        // projects and updates only when they contain something, or need a retry.
        clearProjects();
        $("home-catchup").hidden = true;
      }
      if (onHome) $("home-programme").replaceChildren(make("p", "home-note", "Loading the programme…"));
    }
    if (onCreative && verified && !preserve) {
      unread = []; $("home-catchup-read").hidden = true;
      $("home-catchup-list").replaceChildren(make("p", "home-note", "Checking for new updates…"));
      $("home-catchup-count").textContent = "";
    } else if (!verified) clearPrivate();
    const jobs = [];
    if (onHome) jobs.push(["home-programme", "/api/world/moments/agenda", renderAgenda, "The programme could not be loaded."]);
    if (onCreative) jobs.push(["home-project-list", "/api/journeys/discover?limit=3", renderProjects, "Projects could not be loaded."]);
    if (onCreative && verified) jobs.push(["home-catchup-list", "/api/journeys/updates?unread_only=true&limit=6", renderUpdates, "Your project updates could not be loaded. Your read state is unchanged."]);
    await Promise.all(jobs.map(async ([id, path, render, message]) => {
      try {
        const data = await context.api(path, { signal: flight.controller.signal });
        if (current()) render(data);
      } catch (_) {
        if (current()) {
          if (id === "home-project-list") { projectsLoaded = false; $("home-projects").hidden = false; }
          if (id === "home-programme") $("home-hub").hidden = surface !== "home";
          if (id === "home-catchup-list") {
            clearPrivate(); $("home-catchup").hidden = false;
          }
          failure($(id), message);
        }
      }
    }));
    if (refreshFlight === flight) {
      refreshFlight = null;
      if (refreshPending) queueRefresh();
    }
  }
  function queueRefresh() {
    if (!context || !visible || document.hidden) return;
    refreshPending = true;
    if (refreshTimer || refreshFlight) return;
    // Do not reset this timer on every global event: sustained activity must not starve reads.
    refreshTimer = setTimeout(() => {
      refreshTimer = null; refreshPending = false;
      if (visible && !document.hidden) refresh({ preserve: true });
    }, 5000);
  }
  function invalidate() {
    if (!context) return;
    ++generation;
    stopRefreshRead();
    clearTimeout(refreshTimer); refreshTimer = null; refreshPending = false;
    // Privacy events invalidate immediately, including requests started before the event.
    clearPrivate();
    clearProjects();
    $("home-programme").replaceChildren();
    $("home-hub").hidden = true;
    if (visible) refreshTimer = setTimeout(() => { refreshTimer = null; refresh(); }, 250);
  }
  function show(where) {
    if (!context) return;
    // "home" or "creative"; a plain true still means Home, anything else hides both surfaces.
    const nextSurface = where === "creative" ? "creative" : where ? "home" : null;
    if (nextSurface && nextSurface === surface && refreshFlight?.key === viewerKey()
        && !refreshFlight.controller.signal.aborted) return;
    surface = nextSurface;
    visible = Boolean(surface);
    // Only the programme lives in the hub now, so renderAgenda shows it once there is something to show.
    $("home-hub").hidden = true;
    $("category-rail")?.classList.toggle("home-categories", surface === "home");
    if (visible) { if (surface !== "home") clearTimeout(agendaTimer); refresh({ preserve: surface === "creative" && projectsLoaded }); }
    else { ++generation; stopRefreshRead(); clearTimeout(refreshTimer); refreshTimer = null; refreshPending = false; clearTimeout(agendaTimer); clearPrivate(); }
  }
  function init(adapter) {
    context = adapter;
    $("home-project-refresh").addEventListener("click", refresh);
    $("home-catchup-read").addEventListener("click", markDisplayedRead);
    $("home-catchup-all").addEventListener("click", () => context.openUpdates());
    document.addEventListener("louderr:projects-read", invalidate);
    document.addEventListener("visibilitychange", () => { if (!document.hidden && visible) refresh(); });
    addEventListener("online", invalidate);
    setInterval(queueRefresh, 60_000);
  }
  window.LouderrHome = { init, show, refresh, invalidate, queueRefresh };
})();
